import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

/** Canonical site origin used to build absolute og:url / og:image URLs.
 *  Falls back to the production domain when VITE_SITE_URL isn't set. */
const SITE_URL = (import.meta.env.VITE_SITE_URL ?? 'https://munifocus.com').replace(/\/$/, '');

interface PageMetaProps {
  /** Translation key for the page title segment (e.g. 'web.about.kicker'). */
  titleKey: string;
  /** Literal title segment, used in place of {@link titleKey} when the value
   *  isn't translatable (e.g. a bulletin headline). Takes precedence. */
  titleText?: string;
  /** Optional translation key for meta description. */
  descriptionKey?: string;
  /** Literal meta description, used in place of {@link descriptionKey}. */
  descriptionText?: string;
  /** Optional social-card image path (absolute or site-relative). Defaults to
   *  the brand card; pass a per-page image (e.g. an article hero) to override. */
  imagePath?: string;
  /** Keep this route out of search indexes (transactional pages: confirm /
   *  unsubscribe landings). Emits `<meta name="robots" content="noindex,…">`. */
  noindex?: boolean;
}

/**
 * Sets <title> as "<page> — MuniFocus" using the current i18n value, keeps
 * <html lang> in sync with the active locale, and emits absolute Open Graph /
 * Twitter card tags (incl. og:image) so link previews render a branded card.
 */
export function PageMeta({
  titleKey,
  titleText,
  descriptionKey,
  descriptionText,
  imagePath,
  noindex,
}: PageMetaProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const segment = titleText ?? t(titleKey);
  const brand = t('web.brand');
  const tagline = t('web.footer.tagline').replace(/\.$/, '');
  const title = segment ? `${brand} | ${segment}` : `${brand} | ${tagline}`;
  const description = descriptionText ?? (descriptionKey ? t(descriptionKey) : tagline);

  // Absolute URLs are required by OG/Twitter crawlers. Build them from the
  // canonical origin and the current route, not window.location.origin (which
  // would leak preview-deploy domains into the shared card).
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const url = `${SITE_URL}${path}`;
  const image = (imagePath ?? '/og-image.png').startsWith('http')
    ? (imagePath as string)
    : `${SITE_URL}${imagePath ?? '/og-image.png'}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {/* The site is content-negotiated (one URL, language by cookie/?lang).
          Point each hreflang at the `?lang=` variant so crawlers can reach both
          locales; x-default is the clean canonical. */}
      <link rel="alternate" hrefLang="en" href={`${url}?lang=en`} />
      <link rel="alternate" hrefLang="fr" href={`${url}?lang=fr`} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={brand} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === 'fr' ? 'fr_CA' : 'en_CA'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
    </Helmet>
  );
}
